import { Checkbox } from "../../components/ui/checkbox";
import { FormattedDescription } from "../../components/FormattedDescription";
import { groupLineItemsBySections, hasSections } from "../../utils/lineItemSections";

const QuoteLineItems = ({
  quote,
  isSigned,
  hasOptionalItems,
  optionalSelections,
  handleOptionalToggle,
  adjustedQuantities = {},
  handleQuantityChange,
  dynamicSubtotal,
  discountAmount,
  discountedSubtotal,
}) => {
  const groups = groupLineItemsBySections(quote.line_items || []);
  const sectionsExist = hasSections(quote.line_items);
  const isLumpSum = quote.pricing_mode === "lump_sum";

  // Effective quantity/total for an item, honoring a customer-adjusted quantity
  // (before signing). After signing we trust the persisted values.
  const effQty = (item) =>
    (!isSigned && item.customer_adjustable_qty && adjustedQuantities[item.id] != null)
      ? (Number(adjustedQuantities[item.id]) || 0)
      : (item.quantity || 0);
  const effTotal = (item) =>
    (!isSigned && item.customer_adjustable_qty && adjustedQuantities[item.id] != null)
      ? (Number(adjustedQuantities[item.id]) || 0) * (item.unit_price || 0)
      : (item.total || 0);

  const isSectionSelected = (header) => {
    if (!header?.optional) return true;
    return isSigned ? header.accepted !== false : optionalSelections[header.id] !== false;
  };

  // Dynamic per-section subtotal respecting customer optional toggles, section
  // lump sum, and section-optional decline.
  const calcSectionSubtotal = (group) => {
    if (!isSectionSelected(group.header)) return 0;
    if (group.header?.section_lump_sum != null && group.header.section_lump_sum !== "") {
      return Number(group.header.section_lump_sum) || 0;
    }
    return group.items.reduce((sum, it) => {
      if (it.optional) {
        const selected = isSigned ? it.accepted !== false : optionalSelections[it.id];
        if (!selected) return sum;
      }
      return sum + effTotal(it);
    }, 0);
  };

  return (
    <div className="p-8 border-b border-zinc-200">
      <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-[#00341d] mb-6">
        {isLumpSum ? "What's Included" : "Services"}
      </h3>

      {hasOptionalItems && !isSigned && !isLumpSum && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-amber-800 text-sm font-medium">
            Some items below are optional. Use the checkboxes to select the services you want included in your quote.
          </p>
        </div>
      )}

      <div className="space-y-6">
        {groups.map((group, gIdx) => {
          const sectionLump = group.header?.section_lump_sum != null && group.header.section_lump_sum !== "";
          const sectionOptional = !!group.header?.optional;
          const sectionSelected = isSectionSelected(group.header);
          const sectionTotal = calcSectionSubtotal(group);
          const hideItemPrices = isLumpSum || sectionLump;
          return (
            <div key={group.header?.id || `ungrouped-${gIdx}`} className={sectionOptional && !sectionSelected ? "opacity-50" : ""} data-testid={group.header ? `view-section-${group.header.id}` : "view-section-ungrouped"}>
              {group.header && (
                <div className="flex items-center justify-between gap-3 mb-3 pb-2 border-b-2 border-[#00341d]/15">
                  <div className="flex items-center gap-3">
                    {sectionOptional && !isSigned && !isLumpSum && (
                      <Checkbox
                        checked={sectionSelected}
                        onCheckedChange={(checked) => handleOptionalToggle(group.header.id, checked)}
                        className="h-5 w-5 border-amber-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                        data-testid={`optional-section-checkbox-${group.header.id}`}
                      />
                    )}
                    <span className={`font-heading uppercase tracking-wider text-sm font-bold ${sectionOptional && !sectionSelected ? "text-zinc-400 line-through" : "text-[#00341d]"}`}>
                      {group.header.service_name || "Section"}
                    </span>
                    {sectionOptional && (
                      <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">Optional</span>
                    )}
                  </div>
                  {sectionLump && !isLumpSum && (
                    <span className={`font-bold text-[#00341d] ${sectionOptional && !sectionSelected ? "text-zinc-300 line-through" : ""}`} data-testid={`view-section-lump-${group.header.id}`}>
                      ${(Number(group.header.section_lump_sum) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  )}
                </div>
              )}

              <div className="space-y-4">
                {group.items.map((item, index) => {
                  const isMobilization = item.service_name?.toLowerCase().includes("mobilization");
                  const showBreakdown = !isMobilization && !item.total_only && !hideItemPrices;
                  const isSelected = item.optional ? optionalSelections[item.id] : true;

                  return (
                    <div
                      key={item.id || index}
                      className={`border-b border-zinc-100 pb-4 last:border-0 transition-opacity ${
                        item.optional && !isSelected ? "opacity-50" : ""
                      }`}
                      data-testid={`line-item-${item.id || index}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1 flex items-start gap-3">
                          {item.optional && !isSigned && !hideItemPrices && (
                            <Checkbox
                              id={`optional-item-${item.id}`}
                              checked={isSelected}
                              onCheckedChange={(checked) => handleOptionalToggle(item.id, checked)}
                              className="mt-1 h-5 w-5 border-amber-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                              data-testid={`optional-checkbox-${item.id}`}
                            />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className={`font-medium ${item.optional && !isSelected ? "text-zinc-400 line-through" : "text-zinc-900"}`}>
                                {item.service_name}
                              </p>
                              {item.optional && !hideItemPrices && (
                                <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">
                                  Optional
                                </span>
                              )}
                            </div>
                            {item.description && (
                              <FormattedDescription
                                text={item.description}
                                className={`text-sm mt-1 ${item.optional && !isSelected ? "text-zinc-300" : "text-zinc-500"}`}
                              />
                            )}
                            {showBreakdown && (effQty(item) > 0 || (item.customer_adjustable_qty && !isSigned && isSelected)) && (
                              item.customer_adjustable_qty && !isSigned && isSelected ? (
                                <div className="mt-2 flex items-center gap-2" data-testid={`qty-adjust-row-${item.id}`}>
                                  <label className="text-sm text-zinc-500">Quantity:</label>
                                  <input
                                    type="number"
                                    min="0"
                                    step="any"
                                    value={adjustedQuantities[item.id] ?? item.quantity ?? 0}
                                    onChange={(e) => handleQuantityChange(item.id, e.target.value === "" ? 0 : parseFloat(e.target.value))}
                                    className="w-24 h-8 px-2 border border-amber-300 rounded bg-amber-50/40 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                                    data-testid={`qty-adjust-input-${item.id}`}
                                  />
                                  <span className="text-sm text-zinc-500">
                                    {item.unit} × ${item.unit_price?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                  </span>
                                </div>
                              ) : (
                                <p className={`text-sm mt-1 ${item.optional && !isSelected ? "text-zinc-300" : "text-zinc-400"}`}>
                                  {effQty(item)} {item.unit} ×{" "}
                                  ${item.unit_price?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </p>
                              )
                            )}
                          </div>
                        </div>
                        {!hideItemPrices && (
                          <div className="text-right">
                            <p className={`font-bold text-lg ${item.optional && !isSelected ? "text-zinc-300 line-through" : "text-[#00341d]"}`}>
                              ${effTotal(item)?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {group.header && sectionsExist && group.items.length > 0 && !isLumpSum && !sectionLump && (
                <div className="flex justify-end mt-3 pt-2 border-t border-[#00341d]/10">
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-zinc-600">Section subtotal</span>
                    <span
                      className="font-semibold text-[#00341d]"
                      data-testid={`view-section-subtotal-${group.header.id}`}
                    >
                      ${sectionTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Totals */}
      <div className="mt-6 flex justify-end">
        <div className="w-80 space-y-2">
          <div className="flex justify-between py-2">
            <span className="text-zinc-600">{isLumpSum ? "Lump Sum" : "Subtotal"}</span>
            <span className="font-medium" data-testid="quote-view-subtotal">
              ${(isSigned || isLumpSum ? quote.subtotal : dynamicSubtotal)?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </span>
          </div>

          {quote.discount_type && (discountAmount > 0 || quote.discount_amount > 0) && (
            <>
              <div className="flex justify-between py-2 text-sm text-green-600">
                <span>
                  Discount{" "}
                  {quote.discount_type === "percentage" ? `(${quote.discount_value}%)` : ""}
                </span>
                <span>
                  -${(isSigned ? quote.discount_amount : discountAmount)?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-zinc-600">After Discount</span>
                <span className="font-medium">
                  ${(isSigned
                    ? (quote.subtotal - quote.discount_amount)
                    : discountedSubtotal
                  )?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>
            </>
          )}

          {(isSigned || hasOptionalItems || quote.discount_type) ? (
            <div className="flex justify-between py-3 border-t-2 border-[#00341d]">
              <span className="font-bold text-lg text-[#00341d]">Total</span>
              <span className="font-bold text-lg text-[#00341d]">
                ${(isSigned
                  ? (quote.subtotal - (quote.discount_amount || 0))
                  : discountedSubtotal
                )?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          ) : (
            <div className="flex justify-between py-3 border-t-2 border-[#00341d]">
              <span className="font-bold text-lg text-[#00341d]">Total</span>
              <span className="font-bold text-lg text-[#00341d]">
                ${quote.subtotal?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteLineItems;
